import { useEffect, useRef } from 'react'
import { gsap } from 'gsap'
import { Check, Star } from 'lucide-react'

const Hero = () => {
  const sectionRef = useRef(null)
  const badgeRef = useRef(null)
  const h1Ref = useRef(null)
  const subtitleRef = useRef(null)
  const ctaRef = useRef(null)
  const trustRef = useRef(null)
  const imageRef = useRef(null)
  const floatBadge1Ref = useRef(null)
  const floatBadge2Ref = useRef(null)

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ delay: 2.5 })

      tl.fromTo(
        badgeRef.current,
        { opacity: 0, y: -20 },
        { opacity: 1, y: 0, duration: 0.5, ease: 'power2.out' }
      )
      .fromTo(
        h1Ref.current,
        { opacity: 0, x: -40 },
        { opacity: 1, x: 0, duration: 0.7, ease: 'power2.out' },
        '-=0.2'
      )
      .fromTo(
        subtitleRef.current,
        { opacity: 0, x: -30 },
        { opacity: 1, x: 0, duration: 0.6, ease: 'power2.out' },
        '-=0.3'
      )
      .fromTo(
        ctaRef.current.children,
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.5, stagger: 0.1, ease: 'power2.out' },
        '-=0.2'
      )
      .fromTo(
        trustRef.current,
        { opacity: 0, y: 15 },
        { opacity: 1, y: 0, duration: 0.5, ease: 'power2.out' },
        '-=0.2'
      )
      .fromTo(
        imageRef.current,
        { opacity: 0, scale: 0.9 },
        { opacity: 1, scale: 1, duration: 0.8, ease: 'power2.out' },
        '-=0.8'
      )
      .fromTo(
        floatBadge1Ref.current,
        { opacity: 0, x: 30 },
        { opacity: 1, x: 0, duration: 0.6, ease: 'power2.out' },
        '-=0.3'
      )
      .fromTo(
        floatBadge2Ref.current,
        { opacity: 0, x: -30 },
        { opacity: 1, x: 0, duration: 0.6, ease: 'power2.out' },
        '-=0.4'
      )
    }, sectionRef)

    return () => ctx.revert()
  }, [])

  const scrollToForm = () => {
    document.getElementById('confianza')?.scrollIntoView({ behavior: 'smooth' })
  }

  const scrollToModules = () => {
    document.getElementById('modulos')?.scrollIntoView({ behavior: 'smooth' })
  }

  return (
    <section
      id="hero"
      ref={sectionRef}
      className="relative overflow-hidden"
      style={{
        paddingTop: 160,
        paddingBottom: 96,
        background: 'linear-gradient(135deg, #0F293E 0%, #1a3a52 100%)',
      }}
    >
      {/* Decorative shapes */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute" style={{ top: '10%', left: '5%', width: 300, height: 300, borderRadius: '50%', background: 'rgba(16,185,129,0.06)' }} />
        <div className="absolute" style={{ bottom: '20%', right: '10%', width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.03)' }} />
        <div className="absolute" style={{ top: '30%', right: '30%', width: 16, height: 16, background: 'rgba(16,185,129,0.12)', transform: 'rotate(45deg)' }} />
        <div className="absolute" style={{ bottom: '40%', left: '15%', width: 12, height: 12, background: 'rgba(255,255,255,0.08)', transform: 'rotate(45deg)' }} />
      </div>

      <div className="container-main relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left: Text */}
          <div>
            <div
              ref={badgeRef}
              className="badge-pill mb-6"
              style={{
                backgroundColor: 'rgba(16,185,129,0.15)',
                color: '#10B981',
                border: '1px solid rgba(16,185,129,0.2)',fontSize:'14pt'
              }}
            >
              <Star size={20} fill="#10B981" />
              REGISTRATE Y SÉ PARTE DE NOSOTROS
            </div>

            <h1
              ref={h1Ref}
              className="font-extrabold text-4xl md:text-5xl leading-tight"
              style={{ color: 'white', lineHeight: 1.1 }}
            >
              Transforma la{' '}
              <span className="relative inline-block">
                Gestión Educativa
                <span
                  className="absolute left-0 bottom-1 h-1 w-full rounded-full"
                  style={{ backgroundColor: '#10B981' }}
                />
              </span>{' '}
              de tu Colegio
            </h1>

            <p
              ref={subtitleRef}
              className="mt-5 text-lg leading-relaxed"
              style={{
                color: 'rgba(255,255,255,0.75)',
                maxWidth: 540,
                lineHeight: 1.7,
              }}
            >
              Centraliza procesos académicos, administrativos y financieros en una sola
              plataforma intuitiva. Reduce costos operativos hasta un 40% y mejora la
              experiencia de estudiantes, docentes y padres de familia.
              {/* Centraliza procesos académicos y administrativos en una sola
              plataforma intuitiva. Reduce costos operativos hasta un 40% y mejora la
              experiencia de estudiantes, docentes y padres de familia. */}
            </p>

            <div ref={ctaRef} className="mt-8 flex flex-wrap gap-4">
              <button onClick={scrollToForm} className="btn-primary">
                Solicitar Información
              </button>
              <button onClick={scrollToModules} className="btn-secondary">
                Ver Módulos
              </button>
            </div>

            <div ref={trustRef} className="mt-10 flex flex-wrap gap-6">
              {[
                'Implementación en 48h',
                'Soporte técnico durante la operación',
                'Sin costos de instalación',
                'Totalmente en la nube'
              ].map((item) => (
                <div key={item} className="flex items-center gap-2">
                  <Check size={16} color="#10B981" strokeWidth={3} />
                  <span className="text-sm font-medium" style={{ color: 'rgba(255,255,255,0.6)' }}>
                    {item}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Image */}
          <div className="relative">
            <div
              ref={imageRef}
              className="floating-animation"
              style={{
                borderRadius: 24,
                overflow: 'hidden',
                boxShadow: '0 20px 50px rgba(0,0,0,0.3)',
                border: '1px solid rgba(255,255,255,0.1)',
              }}
            >
              <img
                src="/alania-agarrando-app.png"
                alt="Olan Academic - Dashboard educativo"
                className="w-full h-auto"
                loading="eager"
              />
            </div>

            {/* Floating badge top-right */}
            <div
              ref={floatBadge1Ref}
              className="absolute hidden md:block"
              style={{
                top: -20,
                right: -20,
                background: 'white',
                borderRadius: 16,
                padding: '16px 20px',
                boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
              }}
            >
              <div className="flex items-center gap-3">
                <div
                  className="flex items-center justify-center rounded-full"
                  style={{ width: 40, height: 40, backgroundColor: '#F0FDF4' }}
                >
                  <span style={{ color: '#10B981', fontSize: 18 }}>🏫</span>
                </div>
                <div>
                  <p className="font-bold text-sm" style={{ color: '#0F293E' }}>
                    +100 Instituciones
                  </p>
                  <p className="text-xs" style={{ color: '#6B7280' }}>
                    Confían en nosotros
                  </p>
                </div>
              </div>
            </div>

            {/* Floating badge bottom-left */}
            <div
              ref={floatBadge2Ref}
              className="absolute hidden md:block"
              style={{
                bottom: -10,
                left: -20,
                background: 'white',
                borderRadius: 16,
                padding: '14px 18px',
                boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
              }}
            >
              <div className="flex items-center gap-3">
                <div
                  className="flex items-center justify-center rounded-full"
                  style={{ width: 36, height: 36, backgroundColor: '#FEF3C7' }}
                >
                  <span style={{ color: '#F59E0B', fontSize: 16 }}>☁️</span>
                </div>
                <div>
                  <p className="font-bold text-sm" style={{ color: '#0F293E' }}>
                    99.9% Uptime
                  </p>
                  <p className="text-xs" style={{ color: '#6B7280' }}>
                    Garantizado
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default Hero
